import React, { FunctionComponent, useMemo } from 'react';
import type { Map } from 'leaflet';

import { NO_SYNC } from 'propTypes/map/mapUrlSync';
import type { Camera } from 'types/camera';
import type { Units } from 'types/units';
import type { UserPermissions } from 'types/permissions';

import SpotMarkerOverlay from './SpotMarkerOverlay';
import ClusterMarkerOverlay from './ClusterMarkerOverlay';

interface SpotData {
  _id: string;
  lat: number;
  lon: number;
  waveHeight: {
    min: number;
    max: number;
  };
  conditions: {
    value: string;
  };
  cameras: Camera[];
  zone?: {
    title: string;
  };
  title?: string;
}

interface ClusterData {
  geometry: {
    coordinates: [number, number];
  };
  properties: {
    spotCount: number;
    worstCondition: string;
    bestCondition: string;
  };
}

interface AnimationsState {
  activeSpotId: string | null;
}

interface MapOverlayProps {
  spots: SpotData[];
  clusters: ClusterData[];
  units: Units;
  animations: AnimationsState;
  permissions: UserPermissions;
  getMarkerStyle: (lat: number, lon: number, spotId?: string) => React.CSSProperties;
  getTooltipPosition: (
    bounds: { N: number; S: number; E: number; W: number },
    spot: { lat: number; lon: number },
  ) => { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
  doSetActiveSpot: (spotId: string) => void;
  doClearActiveSpot: () => void;
  doZoomIntoLocation: (center: { lat: number; lon: number }, syncUrlFlag: boolean) => void;
  onClickSpotMarker: (spot: SpotData) => void;
  syncUrl: string;
  currentSpotId: string | null;
  leafletMapRef: React.MutableRefObject<Map | null>;
  isDragging: boolean;
  isLoadingSpots?: boolean;
}

/**
 * Renders the spots or clusters overlay on the map
 */
const MapOverlay: FunctionComponent<MapOverlayProps> = ({
  spots,
  clusters,
  units,
  animations,
  permissions,
  getMarkerStyle,
  getTooltipPosition,
  doSetActiveSpot,
  doClearActiveSpot,
  doZoomIntoLocation,
  onClickSpotMarker,
  syncUrl,
  currentSpotId,
  leafletMapRef,
  isDragging,
  isLoadingSpots = false,
}) => {
  const clusterOverlays = useMemo(
    () =>
      clusters.map((cluster) => {
        const [lon, lat] = cluster.geometry.coordinates;
        return (
          <ClusterMarkerOverlay
            key={`cluster-${lat}-${lon}`}
            cluster={cluster}
            style={getMarkerStyle(lat, lon)}
            onClick={() => doZoomIntoLocation({ lat, lon }, syncUrl !== NO_SYNC)}
          />
        );
      }),
    [clusters, getMarkerStyle, doZoomIntoLocation, syncUrl],
  );

  const spotOverlays = useMemo(
    () =>
      spots.map((spot) => {
        if (!spot || !spot.lat || !spot.lon) return null;

        const { lat, lon } = spot;
        try {
          if (!leafletMapRef.current) return null;

          const bounds = leafletMapRef.current.getBounds();
          if (!bounds) return null;

          const tooltipPosition = getTooltipPosition(
            {
              N: bounds.getNorth(),
              E: bounds.getEast(),
              S: bounds.getSouth(),
              W: bounds.getWest(),
            },
            spot,
          );

          return (
            <SpotMarkerOverlay
              key={`spot-${spot._id}`}
              spot={spot}
              style={getMarkerStyle(lat, lon, spot._id)}
              units={units}
              animations={animations}
              permissions={permissions}
              tooltipPosition={tooltipPosition}
              onMouseEnter={() => doSetActiveSpot(spot._id)}
              onMouseLeave={() => doClearActiveSpot()}
              onClick={() => onClickSpotMarker(spot)}
              currentSpotId={currentSpotId}
            />
          );
        } catch (_e) {
          return null;
        }
      }),
    [
      animations,
      currentSpotId,
      doClearActiveSpot,
      doSetActiveSpot,
      getMarkerStyle,
      getTooltipPosition,
      leafletMapRef,
      onClickSpotMarker,
      permissions,
      spots,
      units,
    ],
  );

  // Don't render anything if we're dragging, loading spots, or the map isn't initialized
  if (isDragging || isLoadingSpots || !leafletMapRef.current) {
    return null;
  }

  // Render clusters if available
  if (clusters && clusters.length > 0) {
    return <div>{clusterOverlays}</div>;
  }

  // Render spots if available
  if (spots && spots.length > 0) {
    return <div>{spotOverlays}</div>;
  }

  return null;
};

export default MapOverlay;
